// ts-rest contract for the COAC (corporate-actions) surface.
//
// `CoacAction` is the single COAC model — the legacy `coac_cases` mailbox
// lifecycle was retired (it was orphaned once #26/ADR-0005 removed its
// mailbox-poll feeder; the upload driver only ever wrote CoacAction rows).
//
// Ingestion lands via `POST /api/coac/uploads` (multipart, handled outside
// ts-rest). These endpoints are the presentation projection + the lifecycle
// mutations every COAC view (the routing list, the Ops inbox, the Desk inbox)
// drives:
//
//   GET  /api/coac/actions                      — list CoacAction rows
//   POST /api/coac/actions/:id/route-to-im       — Ops routes to the IM
//   POST /api/coac/actions/:id/record-decision   — IM records their decision
//   POST /api/coac/actions/:id/approve-election   — finalise the election
//   POST /api/coac/actions/:id/default-election   — IM elects the default
//
// Each mutation maps onto a CoacAction Action (`route-ca-to-im`,
// `record-coac-decision`, `approve-ca-election`, `default-ca-election`); the
// controller dispatches via `registry.invoke` and the Action's persona
// declaration is the real ACL (ADR-0008).

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ---------------------------------------------------------------------------
// CoacAction row — the presentation projection of the `CoacAction` ontology
// Object Type. This is the single wire shape every CoacAction-backed view
// consumes; the controller maps `ctx.read(CoacAction)` onto it. Field names
// mirror the ontology (camelCase); date fields are plain strings because the
// parser emits naive ISO timestamps (no offset), which `z.string().datetime()`
// would reject.
// ---------------------------------------------------------------------------

export const coacElectionStatusSchema = z.enum([
    'Pending',
    'RoutedToIm',
    'ElectionApproved',
    'ElectionDefaulted',
]);
export type CoacElectionStatus = z.infer<typeof coacElectionStatusSchema>;

// Ops-completion lifecycle (WP-E) — DISTINCT from electionStatus. A CA stays
// `AwaitingResponses` even after every IM has responded; it moves to `Completed`
// only when an Ops user presses Complete. Completed CAs are read-only on the Desk.
export const coacOpsStatusSchema = z.enum(['AwaitingResponses', 'Completed']);
export type CoacOpsStatus = z.infer<typeof coacOpsStatusSchema>;

export const coacRoutingOutcomeSchema = z.enum([
    'auto_routed',
    'partial',
    'ambiguous',
    'unrouted',
    'pending',
]);
export type CoacRoutingOutcome = z.infer<typeof coacRoutingOutcomeSchema>;

export const coacUrgencyRatingSchema = z.enum(['critical', 'high', 'medium', 'low']);
export type CoacUrgencyRating = z.infer<typeof coacUrgencyRatingSchema>;

// How the responsible IM set (`assignedImUserIds`) was derived.
export const coacAssignmentSourceSchema = z.enum(['alias', 'holdings', 'manual']);
export type CoacAssignmentSource = z.infer<typeof coacAssignmentSourceSchema>;

// Ops review gate: `proposed` = auto-derived, awaiting confirmation; `confirmed`
// = Ops-verified or a manual route.
export const coacAssignmentStatusSchema = z.enum(['proposed', 'confirmed']);
export type CoacAssignmentStatus = z.infer<typeof coacAssignmentStatusSchema>;

export const coacActionRowSchema = z.object({
    id: z.string(),
    uploadBatchId: z.string().nullable(),
    securityName: z.string().nullable(),
    securityId: z.string().nullable(),
    eventType: z.string(),
    importKey: z.string(),
    wealthManager: z.string().nullable(),
    responseDeadline: z.string().nullable(),
    managerDecision: z.string().nullable(),
    // COAC Book feed (mandatory CA events) — populated only by the "COAC Book"
    // (Book1.xlsx) upload path; null on COAC Log / PDF rows. Dates are plain
    // strings (naive ISO, no offset), matching `responseDeadline` above.
    swiftTag: z.string().nullable(),
    actionType: z.string().nullable(),
    reference: z.string().nullable(),
    sedol: z.string().nullable(),
    responseRequired: z.boolean().nullable(),
    eligibleUnits: z.number().nullable(),
    unrespondedUnits: z.number().nullable(),
    exDate: z.string().nullable(),
    recordDate: z.string().nullable(),
    effectiveDate: z.string().nullable(),
    payDate: z.string().nullable(),
    electionStatus: coacElectionStatusSchema,
    routingOutcome: coacRoutingOutcomeSchema.nullable(),
    unresolvedManagerTokens: z.array(z.string()).nullable(),
    needsReview: z.boolean().nullable(),
    assigneeId: z.string().nullable(),
    assignedAt: z.string().nullable(),
    assignmentReason: z.string().nullable(),
    // Multi-IM routing (the responsible set + Ops review gate). `assigneeId`
    // above remains the single "lead"; this is the full set every Desk inbox
    // scopes against.
    assignedImUserIds: z.array(z.string()),
    assignmentSource: coacAssignmentSourceSchema.nullable(),
    assignmentStatus: coacAssignmentStatusSchema.nullable(),
    assignmentReviewedAt: z.string().nullable(),
    assignmentReviewedById: z.string().nullable(),
    urgencyRating: coacUrgencyRatingSchema.nullable(),
    sourceLog: z.string().nullable(),
    isDeleted: z.boolean(),
    createdAt: z.string(),
    // Ops-completion lifecycle (WP-E). `opsStatus` null on legacy rows is treated
    // as `AwaitingResponses` by every consumer.
    opsStatus: coacOpsStatusSchema.nullable(),
    opsCompletedAt: z.string().nullable(),
    opsCompletedById: z.string().nullable(),
    // True when the originating CA upload was a PDF whose raw bytes were
    // retained (only `ca_pdf`-sourced actions ever have one). Drives the
    // "View source PDF" affordance on the Desk card.
    hasSourcePdf: z.boolean(),
});
export type CoacActionRow = z.infer<typeof coacActionRowSchema>;

export const coacActionListResponseSchema = z.object({
    actions: z.array(coacActionRowSchema),
});
export type CoacActionListResponse = z.infer<typeof coacActionListResponseSchema>;

// ---------------------------------------------------------------------------
// Lifecycle mutation request / response schemas — mirror the CoacAction
// Action inputs the controller dispatches.
// ---------------------------------------------------------------------------

export const coacRouteToImRequestSchema = z.object({
    // The IM (users row id) Ops picked to own the action. Optional — omitting it
    // keeps whatever CEDAR auto-matched at upload.
    assigneeId: z.string().uuid().optional(),
    note: z.string().optional(),
});
export type CoacRouteToImRequest = z.infer<typeof coacRouteToImRequestSchema>;

// Bulk multi-IM routing (the manual-routing surface). `routeActionsBulk` routes
// every selected CoacAction to one chosen IM set; `routeActionsToHoldersBulk`
// fans each selected action out to all desk IMs holding its security.
export const coacRouteBulkRequestSchema = z.object({
    ids: z.array(z.string().uuid()).min(1),
    imUserIds: z.array(z.string().uuid()).min(1),
});
export type CoacRouteBulkRequest = z.infer<typeof coacRouteBulkRequestSchema>;

export const coacRouteToHoldersBulkRequestSchema = z.object({
    ids: z.array(z.string().uuid()).min(1),
});
export type CoacRouteToHoldersBulkRequest = z.infer<typeof coacRouteToHoldersBulkRequestSchema>;

// Ops review gate: confirm a proposed assignment, optionally correcting the set.
export const coacConfirmAssignmentRequestSchema = z.object({
    imUserIds: z.array(z.string().uuid()).min(1).optional(),
});
export type CoacConfirmAssignmentRequest = z.infer<typeof coacConfirmAssignmentRequestSchema>;

export const coacRecordDecisionRequestSchema = z.object({
    decision: z.string().min(1),
    force: z.boolean().optional(),
});
export type CoacRecordDecisionRequest = z.infer<typeof coacRecordDecisionRequestSchema>;

export const coacApproveElectionRequestSchema = z.object({
    approvalNote: z.string().optional(),
});
export type CoacApproveElectionRequest = z.infer<typeof coacApproveElectionRequestSchema>;

export const coacDefaultElectionRequestSchema = z.object({
    defaultReason: z.string().optional(),
});
export type CoacDefaultElectionRequest = z.infer<typeof coacDefaultElectionRequestSchema>;

const coacActionOkSchema = z.object({ ok: z.literal(true) });
import { errorBodyWithCode as errorBody } from '../schemas/common';

// ---------------------------------------------------------------------------
// Exposure — for a CA security, who holds it and under what service mandate
// (discretionary / advisory / execution-only). IM-scoped server-side: a Desk IM
// sees only their own book; Ops / leadership see firm-wide. Backs the command
// centre's expandable row (the per-action breakdown) and its list-level chips
// + XO sort/filter (the batch summary). Read through Drizzle joins, not the
// ontology, so this is firm-infrastructure aggregation rather than `ctx.read`.
// ---------------------------------------------------------------------------

export const coacServiceTypeSchema = z.enum(['Discretionary', 'Advisory', 'ExecutionOnly']);
export type CoacServiceType = z.infer<typeof coacServiceTypeSchema>;

export const coacExposureBucketSchema = z.object({
    units: z.number(),
    valueGbp: z.number(),
    accounts: z.number(),
});
export type CoacExposureBucket = z.infer<typeof coacExposureBucketSchema>;

export const coacExposureSummarySchema = z.object({
    securityId: z.string(),
    discretionary: coacExposureBucketSchema,
    advisory: coacExposureBucketSchema,
    executionOnly: coacExposureBucketSchema,
    clientCount: z.number(),
    // Distinct risk bands (ClientAccount.riskBand — Growth / Balanced /
    // Adventurous / Cautious / Conservative) present in this security's IM-scoped
    // holdings. Drives the list-level risk filter. Execution-only holdings carry
    // no managed risk band, so they contribute nothing here.
    riskBands: z.array(z.string()),
});
export type CoacExposureSummary = z.infer<typeof coacExposureSummarySchema>;

export const coacExposureSummaryRequestSchema = z.object({
    securityIds: z.array(z.string()).max(500),
});
export type CoacExposureSummaryRequest = z.infer<typeof coacExposureSummaryRequestSchema>;

export const coacExposureLineSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    portfolioRef: z.string(),
    accountType: z.string().nullable(),
    mandateType: z.string().nullable(),
    serviceType: coacServiceTypeSchema,
    // The managed risk band (ClientAccount.riskBand) — the second decision axis
    // alongside serviceType. Null on holdings with no banded mandate (most
    // execution-only accounts). Lets the IM elect per (service × risk) group,
    // e.g. one option for Discretionary · Balanced, another for Discretionary · Growth.
    riskBand: z.string().nullable(),
    quantity: z.number(),
    valuationGbp: z.number(),
});
export type CoacExposureLine = z.infer<typeof coacExposureLineSchema>;

export const coacExposureBreakdownSchema = z.object({
    actionId: z.string(),
    securityId: z.string().nullable(),
    securityName: z.string().nullable(),
    lines: z.array(coacExposureLineSchema),
});
export type CoacExposureBreakdown = z.infer<typeof coacExposureBreakdownSchema>;

// ---------------------------------------------------------------------------
// Per-account elections (WP-E) — the per-account decision model. One row per
// (coacActionId, portfolioRef): the decision an IM recorded on a single
// account's holding. `isNoVote` flags a deadline-passed undecided account.
// ---------------------------------------------------------------------------

export const coacAccountElectionRowSchema = z.object({
    id: z.string(),
    coacActionId: z.string(),
    portfolioRef: z.string(),
    clientId: z.string(),
    imUserId: z.string().nullable(),
    mandateType: z.string().nullable(),
    serviceType: coacServiceTypeSchema,
    decision: z.string().nullable(),
    isNoVote: z.boolean(),
    decidedByUserId: z.string().nullable(),
    decidedAt: z.string(),
});
export type CoacAccountElectionRow = z.infer<typeof coacAccountElectionRowSchema>;

// Record one account's election. The Desk ticks an account in the holdings
// breakdown and picks its decision; the controller maps this onto the
// RecordCoacAccountElection Action input (denormalising clientId / imUserId /
// mandateType / serviceType from the live exposure line).
export const coacRecordAccountElectionRequestSchema = z.object({
    portfolioRef: z.string().min(1),
    clientId: z.string().min(1),
    imUserId: z.string().uuid().nullable().optional(),
    mandateType: z.string().nullable().optional(),
    serviceType: coacServiceTypeSchema,
    decision: z.string().min(1),
});
export type CoacRecordAccountElectionRequest = z.infer<
    typeof coacRecordAccountElectionRequestSchema
>;

// Per-account election plus the joined client name (for the per-IM breakdown +
// the Responses tab, which render the person, not just the portfolioRef).
export const coacAccountElectionViewSchema = coacAccountElectionRowSchema.extend({
    clientName: z.string(),
});
export type CoacAccountElectionView = z.infer<typeof coacAccountElectionViewSchema>;

// One assigned IM's response state on a CA, for the expandable per-IM breakdown
// and the Routing list's "Responded" / "Voted" columns. `respondedAccounts` /
// `totalAccounts` drive the "8/10" per-IM rollup; `initials` feeds the list-
// level "JS, NM" responded column.
export const coacImResponseSchema = z.object({
    imUserId: z.string(),
    imName: z.string(),
    initials: z.string(),
    respondedAccounts: z.number(),
    totalAccounts: z.number(),
    hasResponded: z.boolean(),
    // The account-level elections this IM has recorded (with client names),
    // grouped under the IM in the breakdown.
    elections: z.array(coacAccountElectionViewSchema),
});
export type CoacImResponse = z.infer<typeof coacImResponseSchema>;

// The full per-CA responses breakdown: the voted/total roll-up + the per-IM
// detail. Backs the Ops Routing list's expandable row and feeds the columns.
export const coacResponsesBreakdownSchema = z.object({
    actionId: z.string(),
    votedAccounts: z.number(),
    totalAccounts: z.number(),
    // Distinct IM initials that have fully responded (all their accounts decided).
    respondedInitials: z.array(z.string()),
    ims: z.array(coacImResponseSchema),
});
export type CoacResponsesBreakdown = z.infer<typeof coacResponsesBreakdownSchema>;

// Compact per-CA response summary for the Routing list's columns (voted/total +
// responded IM initials), batched across every CA in one call.
export const coacResponsesSummaryRowSchema = z.object({
    actionId: z.string(),
    votedAccounts: z.number(),
    totalAccounts: z.number(),
    respondedInitials: z.array(z.string()),
});
export type CoacResponsesSummaryRow = z.infer<typeof coacResponsesSummaryRowSchema>;

// One flat row for the Ops "Responses" tab: every account election across every
// CA, joined to its CA + client + IM for a filterable table.
export const coacResponsesRowSchema = z.object({
    actionId: z.string(),
    securityName: z.string().nullable(),
    securityId: z.string().nullable(),
    eventType: z.string(),
    portfolioRef: z.string(),
    clientId: z.string(),
    clientName: z.string(),
    imUserId: z.string().nullable(),
    imName: z.string().nullable(),
    serviceType: coacServiceTypeSchema,
    mandateType: z.string().nullable(),
    decision: z.string().nullable(),
    isNoVote: z.boolean(),
    decidedAt: z.string(),
    opsStatus: coacOpsStatusSchema.nullable(),
});
export type CoacResponsesRow = z.infer<typeof coacResponsesRowSchema>;

// Complete a CA: the controller writes No-Vote elections for any undecided
// account whose deadline has passed, then dispatches CompleteCoacAction.
export const coacCompleteActionRequestSchema = z.object({
    note: z.string().optional(),
});
export type CoacCompleteActionRequest = z.infer<typeof coacCompleteActionRequestSchema>;

// The Titan custodian email body generated alongside the Excel on Complete.
export const coacTitanEmailSchema = z.object({
    actionId: z.string(),
    subject: z.string(),
    body: z.string(),
    // The count of account elections the email covers (for the UI summary).
    accountCount: z.number(),
});
export type CoacTitanEmail = z.infer<typeof coacTitanEmailSchema>;

// ---------------------------------------------------------------------------
// CoacActionDraft — AgentArtefact + Reviewable CA draft emitted by MASON's
// `coac_pdf_extraction` job. (ADR-0016 #271 #272)
//
// Approve / reject go through the generic `/agent/reviewables/decisions`
// pipeline (kind `coac-action-draft`) — see `apps/web/src/lib/agent-reviewables.ts`
// — not a bespoke route here. `listActionDrafts` below is a read-only listing.
// ---------------------------------------------------------------------------

export const coacActionDraftRowSchema = z.object({
    id: z.string(),
    securityName: z.string(),
    securityId: z.string().nullable(),
    eventType: z.string(),
    sourceReferenceId: z.string(),
    responseDeadline: z.string().nullable(),
    narrative: z.string().nullable(),
    wealthManager: z.string().nullable(),
    sourceUploadIds: z.array(z.string()),
    fromEmail: z.string().nullable(),
    messageSubject: z.string().nullable(),
    messageReceivedAt: z.string().nullable(),
    correctionNote: z.string().nullable(),
    materialisedCoacActionId: z.string().nullable(),
    createdAt: z.string(),
    reviewStatus: z.enum(['pending', 'approved', 'rejected', 'revision_requested']).nullable(),
    reviewedBy: z.string().nullable(),
    reviewedAt: z.string().nullable(),
    rejectionReason: z.string().nullable(),
});
export type CoacActionDraftRow = z.infer<typeof coacActionDraftRowSchema>;

// ---------------------------------------------------------------------------
// Ops notifications — the in-app inbox surfaced as a bell on the Corporate
// Actions header. First kind: 'coac_votes_complete'.
// ---------------------------------------------------------------------------

export const opsNotificationSchema = z.object({
    id: z.string(),
    kind: z.string(),
    title: z.string(),
    body: z.string().nullable(),
    coacActionId: z.string().nullable(),
    createdAt: z.string(),
    readAt: z.string().nullable(),
});
export type OpsNotification = z.infer<typeof opsNotificationSchema>;

export const opsNotificationsResponseSchema = z.object({
    notifications: z.array(opsNotificationSchema),
    unread: z.number(),
});

export const markNotificationsReadRequestSchema = z.object({
    // Mark a single notification read, or all unread when omitted.
    id: z.string().optional(),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const coacContract = c.router(
    {
        listActions: {
            method: 'GET',
            path: '/coac/actions',
            responses: {
                200: coacActionListResponseSchema,
            },
            summary:
                'CoacAction rows (presentation projection) the routing list and the Ops/Desk inboxes render',
        },
        exposureSummary: {
            method: 'POST',
            path: '/coac/exposure/summary',
            body: coacExposureSummaryRequestSchema,
            responses: {
                200: z.object({ summaries: z.array(coacExposureSummarySchema) }),
            },
            summary:
                'IM-scoped per-security exposure summary (discretionary / advisory / execution-only units, value, accounts) for a set of securities',
        },
        actionExposure: {
            method: 'GET',
            path: '/coac/actions/:id/exposure',
            pathParams: z.object({ id: z.string() }),
            responses: {
                200: coacExposureBreakdownSchema,
                404: errorBody,
            },
            summary:
                "IM-scoped holdings breakdown for one CoacAction's security (per-client/account lines split by service mandate)",
        },
        rerouteActions: {
            method: 'POST',
            path: '/coac/actions/reroute',
            body: z.object({}).optional(),
            responses: {
                200: z.object({ ok: z.literal(true), rerouted: z.number() }),
            },
            summary:
                'Re-run CEDAR deterministic routing over all Pending CoacAction rows (alias-directory based; no LLM)',
        },
        routeActionToIm: {
            method: 'POST',
            path: '/coac/actions/:id/route-to-im',
            pathParams: z.object({ id: z.string() }),
            body: coacRouteToImRequestSchema,
            responses: {
                200: coacActionOkSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Ops routes a CoacAction to the responsible IM (Pending -> RoutedToIm)',
        },
        routeActionsBulk: {
            method: 'POST',
            path: '/coac/actions/route-bulk',
            body: coacRouteBulkRequestSchema,
            responses: {
                200: z.object({ ok: z.literal(true), routed: z.number() }),
                400: errorBody,
                403: errorBody,
            },
            summary: 'Ops routes many selected CoacActions to one chosen IM set (multi-select)',
        },
        routeActionsToHoldersBulk: {
            method: 'POST',
            path: '/coac/actions/route-to-holders-bulk',
            body: coacRouteToHoldersBulkRequestSchema,
            responses: {
                200: z.object({
                    ok: z.literal(true),
                    routed: z.number(),
                    // Selected actions whose security had no desk IM holder — Ops
                    // must route these manually.
                    withoutHolders: z.array(z.string()),
                }),
                400: errorBody,
                403: errorBody,
            },
            summary:
                'Ops fans each selected CoacAction out to all desk IMs holding its company (multi-select)',
        },
        confirmActionAssignment: {
            method: 'POST',
            path: '/coac/actions/:id/confirm-assignment',
            pathParams: z.object({ id: z.string() }),
            body: coacConfirmAssignmentRequestSchema,
            responses: {
                200: coacActionOkSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Ops confirms a proposed CoacAction IM assignment (optionally correcting it)',
        },
        recordActionDecision: {
            method: 'POST',
            path: '/coac/actions/:id/record-decision',
            pathParams: z.object({ id: z.string() }),
            body: coacRecordDecisionRequestSchema,
            responses: {
                200: coacActionOkSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'IM records their decision text on a CoacAction (force=true to overwrite)',
        },
        approveActionElection: {
            method: 'POST',
            path: '/coac/actions/:id/approve-election',
            pathParams: z.object({ id: z.string() }),
            body: coacApproveElectionRequestSchema,
            responses: {
                200: coacActionOkSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Finalise the election on a CoacAction (terminal: ElectionApproved)',
        },
        defaultActionElection: {
            method: 'POST',
            path: '/coac/actions/:id/default-election',
            pathParams: z.object({ id: z.string() }),
            body: coacDefaultElectionRequestSchema,
            responses: {
                200: coacActionOkSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'IM elects the default option on a CoacAction (terminal: ElectionDefaulted)',
        },

        // -----------------------------------------------------------------
        // Per-account elections (WP-E) — the per-account decision model.
        // -----------------------------------------------------------------
        listAccountElections: {
            method: 'GET',
            path: '/coac/actions/:id/account-elections',
            pathParams: z.object({ id: z.string() }),
            responses: {
                200: z.object({ elections: z.array(coacAccountElectionViewSchema) }),
                404: errorBody,
            },
            summary:
                'The per-account elections recorded against one CoacAction (with client names)',
        },
        recordAccountElection: {
            method: 'POST',
            path: '/coac/actions/:id/account-elections',
            pathParams: z.object({ id: z.string() }),
            body: coacRecordAccountElectionRequestSchema,
            responses: {
                200: z.object({ ok: z.literal(true), id: z.string() }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                "Record one account's election on a CoacAction (IM ticks an account, picks a decision)",
        },
        actionBreakdown: {
            method: 'POST',
            path: '/coac/actions/:id/breakdown',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}),
            responses: {
                202: z.object({ jobId: z.string() }),
                404: errorBody,
            },
            summary:
                "Queue a CEDAR coac_breakdown AgentJob for a corporate action (IM's My decisions view); poll the GET endpoint for the persisted result",
        },
        actionBreakdownLatest: {
            method: 'GET',
            path: '/coac/actions/:id/breakdown',
            pathParams: z.object({ id: z.string() }),
            responses: {
                200: z.object({
                    breakdown: z
                        .object({
                            id: z.string(),
                            breakdown: z.string(),
                            generatedAt: z.string(),
                        })
                        .nullable(),
                }),
                404: errorBody,
            },
            summary:
                'The most recent persisted CEDAR breakdown for a corporate action (null when none has been generated)',
        },
        actionResponses: {
            method: 'GET',
            path: '/coac/actions/:id/responses',
            pathParams: z.object({ id: z.string() }),
            responses: {
                200: coacResponsesBreakdownSchema,
                404: errorBody,
            },
            summary:
                'Per-IM response breakdown for one CoacAction (voted/total, responded initials, per-account decisions)',
        },
        listResponses: {
            method: 'GET',
            path: '/coac/responses',
            responses: {
                200: z.object({ rows: z.array(coacResponsesRowSchema) }),
            },
            summary:
                'Flat per-account election rows across every CA — the Ops Responses tab (filterable)',
        },
        responsesSummary: {
            method: 'GET',
            path: '/coac/responses/summary',
            responses: {
                200: z.object({ rows: z.array(coacResponsesSummaryRowSchema) }),
            },
            summary:
                'Per-CA response roll-up (voted/total accounts + responded IM initials) for the Routing list columns',
        },
        actionTitanEmail: {
            method: 'GET',
            path: '/coac/actions/:id/titan-email',
            pathParams: z.object({ id: z.string() }),
            responses: {
                200: coacTitanEmailSchema,
                404: errorBody,
            },
            summary: "The drafted Titan custodian email body covering a CA's per-account elections",
        },
        completeAction: {
            method: 'POST',
            path: '/coac/actions/:id/complete',
            pathParams: z.object({ id: z.string() }),
            body: coacCompleteActionRequestSchema,
            responses: {
                200: z.object({ ok: z.literal(true), noVoteCount: z.number() }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Ops finalises a CA (No-Vote sweep on deadline-passed accounts, then opsStatus -> Completed). Desk goes read-only.',
        },

        // -----------------------------------------------------------------
        // CoacActionDraft — AgentArtefact + Reviewable CA drafts emitted by
        // MASON's `coac_pdf_extraction` job (ADR-0016 #271 #272). Approve /
        // reject go through `POST /agent/reviewables/decisions` (kind
        // `coac-action-draft`), not a bespoke route — this is read-only.
        // -----------------------------------------------------------------
        listActionDrafts: {
            method: 'GET',
            path: '/coac/action-drafts',
            responses: {
                200: z.object({
                    drafts: z.array(coacActionDraftRowSchema),
                }),
            },
            summary:
                'List CoacActionDraft rows (MASON Haiku-extracted CA notices, any review status)',
        },

        // -----------------------------------------------------------------
        // Ops notifications inbox (the header bell).
        // -----------------------------------------------------------------
        listNotifications: {
            method: 'GET',
            path: '/coac/notifications',
            responses: {
                200: opsNotificationsResponseSchema,
            },
            summary: 'Ops notifications inbox (most-recent first) plus the unread count',
        },
        markNotificationsRead: {
            method: 'POST',
            path: '/coac/notifications/read',
            body: markNotificationsReadRequestSchema,
            responses: {
                200: z.object({ ok: z.literal(true) }),
            },
            summary: 'Mark one Ops notification (by id) or all unread ones as read',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
